import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ImageUpload } from "@/components/ImageUpload";

const EMPTY = { title: "", description: "", location: "", year: "", image: "", story: "", before_image: "", after_image: "", gallery: [], published: true };

export default function WorksManager() {
  const [works, setWorks] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/works/all").then((r) => setWorks(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (w) => {
    setEditing(w);
    setForm({ title: w.title, description: w.description, location: w.location, year: w.year, image: w.image, story: w.story || "", before_image: w.before_image || "", after_image: w.after_image || "", gallery: w.gallery || [], published: w.published });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`/works/${editing.id}`, form);
      else await api.post("/works", form);
      toast.success(editing ? "Lavoro aggiornato" : "Lavoro pubblicato");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    await api.delete(`/works/${id}`).catch(() => {});
    toast.success("Lavoro eliminato");
    load();
  };

  const field = "border-line bg-sand/70 text-forest";

  return (
    <section data-testid="works-manager">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-3xl text-forest">Lavori del portfolio</h2>
        <button onClick={openNew} data-testid="work-new-button" className="flex items-center gap-2 rounded-full bg-forest px-5 py-2.5 font-accent text-xs uppercase tracking-[0.18em] text-bone transition-transform duration-300 hover:scale-105 active:scale-95">
          <Plus className="h-4 w-4" /> Nuovo lavoro
        </button>
      </div>
      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {works.map((w) => (
          <div key={w.id} className="group rounded-sm border border-line bg-bone p-4 transition-transform duration-300 hover:-translate-y-1" data-testid={`work-row-${w.id}`}>
            <img src={w.image} alt={w.title} className="aspect-[16/10] w-full rounded-sm bg-sand object-cover" />
            <div className="mt-4 flex items-start justify-between gap-2">
              <div>
                <p className="font-serif text-lg leading-tight text-forest">{w.title}</p>
                <p className="mt-1 font-accent text-[10px] uppercase tracking-[0.2em] text-sage">{w.location} · {w.year}</p>
              </div>
              <span className={`shrink-0 rounded-full px-2.5 py-1 font-accent text-[9px] uppercase tracking-[0.15em] ${w.published ? "bg-moss/15 text-moss" : "bg-sand text-sage"}`}>
                {w.published ? "Online" : "Bozza"}
              </span>
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={() => openEdit(w)} className="flex-1 rounded-full border border-line py-2 font-accent text-[10px] uppercase tracking-[0.15em] text-forest hover:bg-sand" data-testid={`work-edit-${w.id}`}>
                <Pencil className="mr-1 inline h-3 w-3" /> Modifica
              </button>
              <button onClick={() => del(w.id)} className="rounded-full border border-line px-3 py-2 text-destructive hover:bg-sand" data-testid={`work-delete-${w.id}`} aria-label="Elimina">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
      {works.length === 0 && <p className="mt-8 rounded-sm border border-line bg-bone px-5 py-10 text-center text-sage">Nessun lavoro. Aggiungi il primo.</p>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-bone" data-testid="work-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-forest">{editing ? "Modifica lavoro" : "Nuovo lavoro"}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-5 pt-2">
            <div>
              <Label className="font-accent text-xs uppercase tracking-[0.18em] text-sage">Titolo *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={`mt-2 ${field}`} data-testid="work-form-title" />
            </div>
            <div className="grid gap-5 md:grid-cols-2">
              <div>
                <Label className="font-accent text-xs uppercase tracking-[0.18em] text-sage">Luogo</Label>
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Cagliari" className={`mt-2 ${field}`} data-testid="work-form-location" />
              </div>
              <div>
                <Label className="font-accent text-xs uppercase tracking-[0.18em] text-sage">Anno</Label>
                <Input value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} placeholder="2026" className={`mt-2 ${field}`} data-testid="work-form-year" />
              </div>
            </div>
            <ImageUpload label="Immagine di copertina" value={form.image} onChange={(v) => setForm({ ...form, image: v })} testid="work-form-image" />
            <div>
              <Label className="font-accent text-xs uppercase tracking-[0.18em] text-sage">Descrizione</Label>
              <Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`mt-2 ${field}`} data-testid="work-form-description" />
            </div>
            <div>
              <Label className="font-accent text-xs uppercase tracking-[0.18em] text-sage">Racconto del progetto (pagina dettaglio)</Label>
              <Textarea rows={5} value={form.story} onChange={(e) => setForm({ ...form, story: e.target.value })} placeholder="Le esigenze del cliente, le scelte fatte, le piante usate…" className={`mt-2 ${field}`} data-testid="work-form-story" />
            </div>
            <div className="grid gap-5 md:grid-cols-2">
              <ImageUpload label="Foto PRIMA" value={form.before_image} onChange={(v) => setForm({ ...form, before_image: v })} testid="work-form-before" />
              <ImageUpload label="Foto DOPO" value={form.after_image} onChange={(v) => setForm({ ...form, after_image: v })} testid="work-form-after" />
            </div>
            <div>
              <Label className="font-accent text-xs uppercase tracking-[0.18em] text-sage">Galleria</Label>
              <div className="mt-2 flex flex-col gap-3">
                {form.gallery.map((g, idx) => (
                  <div key={idx} className="flex items-center gap-3" data-testid={`work-gallery-item-${idx}`}>
                    <img src={g} alt="" className="h-12 w-12 shrink-0 rounded-sm bg-sand object-cover" />
                    <span className="flex-1 truncate text-xs text-sage">{g}</span>
                    <button type="button" onClick={() => setForm({ ...form, gallery: form.gallery.filter((_, i) => i !== idx) })} className="rounded-full border border-line p-1.5 text-destructive" data-testid={`work-gallery-remove-${idx}`} aria-label="Rimuovi">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                <ImageUpload label="Aggiungi foto alla galleria" value="" onChange={(v) => v && setForm({ ...form, gallery: [...form.gallery, v] })} testid="work-gallery-add" />
              </div>
            </div>
            <label className="flex items-center gap-3 text-sm text-forest">
              <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="work-form-published" /> Visibile nel portfolio
            </label>
            <button onClick={save} disabled={saving || !form.title} data-testid="work-form-save" className="flex items-center justify-center gap-2 rounded-full bg-forest py-3.5 font-accent text-sm uppercase tracking-[0.2em] text-bone transition-transform duration-300 hover:scale-[1.01] active:scale-95 disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Salva
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
